import { age } from "./body";
import { addDays, dayLabel, dayMonth, daysBetween } from "./date";
import { OLDER_AGE, older, type DeviceAnswers } from "./eating";
import { basedOn, type Insight } from "./insight";
import type { DayTests, JointAnswer, KayaState, PhaseIndex, TrainingProfile } from "./types";

/**
 * The self-tests a phone can run, their published ranges, and what the results place (ADR-054;
 * plan 2026-10-05 §3.4; `older-adults-guidelines.md` §9, §D; `plans-by-age-sex-goal.md` §3.5).
 *
 * A result is shown as the person's figure beside the range for their age and sex with its source
 * named — never as a grade, never as "below average" or "poor", and never as a fitness age. The
 * norms are US norms (Rikli & Jones; CDC STEADI); none exist for India, and the screens say so.
 */

/** The questionnaire's tests step (P3) names these; `tug` is offered on the tests' own intro. */
export type TestKey = "chair" | "balance" | "pushups" | "plank" | "step";
export type TestId = TestKey | "tug";

export type Sex = "male" | "female";

/* ---------- the protocols, as data ---------- */

export interface TestProtocol {
  title: string;
  /** the intro row's one line */
  sub: string;
  /** what the clock does: a countdown with a tap count, the four stages, a stopwatch, a count */
  kind: "countdown" | "stages" | "stopwatch" | "count";
  /** the countdown's length */
  secs?: number;
  /** the rule line read before the clock starts */
  rule: string;
  /** the button the person beside taps, for a countdown */
  tap?: string;
  source: string | null;
}

export const TESTS: Record<TestId, TestProtocol> = {
  chair: {
    title: "Chair stand",
    sub: "Arms crossed, stand up and sit down as often as you can in 30 seconds. Kaya times; the person beside you counts.",
    kind: "countdown",
    secs: 30,
    rule: "Arms crossed; stop and score 0 if the arms are needed.",
    tap: "Tap at each full stand",
    source: "CDC STEADI",
  },
  balance: {
    title: "Balance",
    sub: "Four ways of standing, 10 seconds each, a counter in reach.",
    kind: "stages",
    secs: 10,
    rule: "Feet still, eyes open, hands near the counter but not on it.",
    source: "CDC STEADI",
  },
  tug: {
    title: "Up and go",
    sub: "Stand, walk 3 metres, turn, walk back and sit. Kaya times it.",
    kind: "stopwatch",
    rule: "Walk at your usual pace; a stick or frame is allowed.",
    source: "CDC STEADI",
  },
  step: {
    title: "2-minute step",
    sub: "March on the spot for two minutes; the person beside you counts the right knee.",
    kind: "countdown",
    secs: 120,
    rule: "Knees to halfway between kneecap and hip; a hand on the wall is fine.",
    tap: "Tap at each right knee",
    source: "Rikli & Jones",
  },
  pushups: {
    title: "Wall push-ups",
    sub: "As many as you can with good form.",
    kind: "count",
    rule: "Hands on the wall at shoulder height, body straight; stop when the form goes.",
    tap: "Tap at each push-up",
    source: null,
  },
  plank: {
    title: "Plank",
    sub: "Held as long as your back stays straight.",
    kind: "stopwatch",
    rule: "On the forearms; keep breathing, and stop when the back sags.",
    source: null,
  },
};

/** The four stages of the balance test, in STEADI's order. */
export const BALANCE_STAGES: { name: string; sub: string; cue: string }[] = [
  { name: "Feet together", sub: "side by side", cue: "Feet side by side. Hands near the counter, not on it." },
  { name: "Semi-tandem", sub: "instep by the big toe", cue: "The instep of one foot against the big toe of the other." },
  { name: "Heel to toe", sub: "one foot in front", cue: "Front heel touching the back toes. Hands near the counter, not on it." },
  { name: "One leg", sub: "the other foot lifted", cue: "Stand on one foot. Hands near the counter, not on it." },
];

/** The heel-to-toe stand held this long is STEADI's line; under it is "an increased risk of falling". */
export const TANDEM_SECS = 10;
/** Timed up-and-go at or over this is STEADI's "at risk" (§9.3). The World Falls Guidelines' line is
 *  over 15 s; Kaya quotes one, STEADI's, and names it. */
export const TUG_LINE = 12;
export const TUG_LINE_WFG = 15;

/* ---------- the tables (older-adults-guidelines.md §9.1, §9.4) ---------- */

type Row = [from: number, low: number, high: number];

/** 30-second chair stand. `low` is both STEADI's line (a count under it is STEADI's "below average")
 *  and the start of the SFT normal range (25th–75th percentile, Rikli & Jones, US). */
const CHAIR: Record<Sex, Row[]> = {
  male: [[90, 7, 12], [85, 8, 14], [80, 10, 15], [75, 11, 17], [70, 12, 17], [65, 12, 18], [60, 14, 19]],
  female: [[90, 4, 11], [85, 8, 13], [80, 9, 14], [75, 10, 15], [70, 10, 15], [65, 11, 16], [60, 12, 17]],
};

/** 2-minute step test, right-knee lifts: the SFT normal range (Rikli & Jones, US). */
const STEP: Record<Sex, Row[]> = {
  male: [[90, 52, 86], [85, 59, 91], [80, 71, 103], [75, 73, 109], [70, 80, 110], [65, 86, 116], [60, 87, 115]],
  female: [[90, 44, 72], [85, 55, 85], [80, 60, 91], [75, 68, 100], [70, 68, 101], [65, 73, 107], [60, 75, 107]],
};

export interface TestRange {
  /** "65–69" */
  band: string;
  /** "women" / "men" */
  who: string;
  low: number;
  high: number;
  /** a count under `line` is the source's cut-off (the chair stand's STEADI line); null when the
   *  source gives a range only */
  line: number | null;
  source: string;
}

function asSex(sex: string | undefined): Sex | null {
  return sex === "male" || sex === "female" ? sex : null;
}

function bandWord(from: number): string {
  return from >= 90 ? "90 and over" : `${from}–${from + 4}`;
}

/**
 * The published range for a test at an age and sex, or null when there is none: under 60 (the
 * tables start there), without a sex, and always for the wall push-ups and the plank, which have
 * no published age norms. The balance test and the up-and-go have a line, not a range: read
 * `TANDEM_SECS` and `TUG_LINE`.
 */
export function rangeFor(test: TestId, years: number | null, sex: string | undefined): TestRange | null {
  const s = asSex(sex);
  if (years === null || years < OLDER_AGE || !s) return null;
  const table = test === "chair" ? CHAIR : test === "step" ? STEP : null;
  if (!table) return null;
  const row = table[s].find(([from]) => years >= from);
  if (!row) return null;
  return {
    band: bandWord(row[0]),
    who: s === "female" ? "women" : "men",
    low: row[1],
    high: row[2],
    line: test === "chair" ? row[1] : null,
    source: test === "chair" ? "CDC STEADI" : "Rikli & Jones, US norms",
  };
}

/** The chair stand's STEADI line for this person, or null under 60 or without a sex. */
export function chairStandLine(S: KayaState, today = new Date()): number | null {
  return rangeFor("chair", age(S, today), S.profile.sex)?.line ?? null;
}

/* ---------- a reading in words ---------- */

export interface Reading {
  /** "9 stands", "Tandem stand 6 s", "Up and go –" */
  title: string;
  /** the range and its source, or what the figure is: "The usual range for women of 65–69 starts at 11 (CDC STEADI)." */
  detail: string;
  done: boolean;
}

function n(x: number, one: string, many: string): string {
  return `${x} ${x === 1 ? one : many}`;
}

function noRange(years: number | null, sex: string | undefined): string {
  if (years !== null && years < OLDER_AGE) return "Your figure; the published ranges start at 60.";
  if (!asSex(sex)) return "Your figure; the published ranges are by age and sex.";
  return "Your figure; no published range.";
}

/** "The usual range for women of 65–69 starts at 11 (CDC STEADI)." — or, `full`, "is 11–16". */
export function rangeWords(r: TestRange, full = false): string {
  const span = full ? `is ${r.low}–${r.high}` : `starts at ${r.low}`;
  return `The usual range for ${r.who} of ${r.band} ${span} (${r.source}).`;
}

const NOT_DONE = "Not done. It is optional; try it at the retest.";

/**
 * One test's result as the result screen and Progress show it: the figure, then the range with
 * its source — never a grade. A test not done is "–" with why (invariant 11's spirit: not done is
 * not 0). The arms needed on the chair stand score 0 with the note, as STEADI's protocol does.
 */
export function reading(test: TestId, t: Partial<DayTests>, years: number | null, sex: string | undefined): Reading {
  switch (test) {
    case "chair": {
      if (t.chairStand === undefined) return { title: "Chair stand –", detail: "Not done yet.", done: false };
      const r = rangeFor("chair", years, sex);
      return {
        title: `${n(t.chairStand, "stand", "stands")}${t.chairArms ? " · used the arms" : ""}`,
        detail: r ? rangeWords(r) : noRange(years, sex),
        done: true,
      };
    }
    case "balance": {
      if (t.balanceStage === undefined) return { title: "Balance –", detail: "Not done yet.", done: false };
      const s = t.balanceStage;
      const secs = t.balanceSecs ?? 0;
      if (s >= 4) return { title: "One-leg stand 10 s", detail: "All four stands held 10 s each (CDC STEADI).", done: true };
      if (s === 3)
        return {
          title: `Tandem stand ${TANDEM_SECS} s`,
          detail: `Held for the ${TANDEM_SECS} seconds that are the line (CDC STEADI); one leg ${secs} s.`,
          done: true,
        };
      if (s === 2)
        return {
          title: `Tandem stand ${secs} s`,
          detail: "Under the 10-second line: feet together and semi-tandem held 10 s each.",
          done: true,
        };
      return {
        title: `${s === 1 ? "Semi-tandem" : "Feet together"} ${secs} s`,
        detail: `Heel to toe not reached; the line is a ${TANDEM_SECS}-second heel-to-toe stand (CDC STEADI).`,
        done: true,
      };
    }
    case "tug":
      return t.tug === undefined
        ? { title: "Up and go –", detail: NOT_DONE, done: false }
        : { title: `Up and go ${t.tug} s`, detail: `The line is ${TUG_LINE} seconds (CDC STEADI).`, done: true };
    case "step": {
      if (t.step === undefined) return { title: "Step test –", detail: "Not done yet.", done: false };
      const r = rangeFor("step", years, sex);
      return { title: n(t.step, "step", "steps"), detail: r ? rangeWords(r, true) : noRange(years, sex), done: true };
    }
    case "pushups":
      return t.pushups === undefined
        ? { title: "Wall push-ups –", detail: "Not done yet.", done: false }
        : { title: n(t.pushups, "wall push-up", "wall push-ups"), detail: "Your figure; no published range.", done: true };
    case "plank":
      return t.plank === undefined
        ? { title: "Plank –", detail: "Not done yet.", done: false }
        : { title: `Plank ${t.plank} s`, detail: "Your figure; no published range.", done: true };
  }
}

/** "9 stands; the usual range for women of 65–69 starts at 11 (CDC STEADI)" — one line. */
export function readingLine(test: TestId, t: Partial<DayTests>, years: number | null, sex: string | undefined): string {
  const r = reading(test, t, years, sex);
  const d = r.detail.replace(/\.$/, "");
  return `${r.title}; ${d.charAt(0).toLowerCase()}${d.slice(1)}`;
}

/* ---------- which tests, and which are left out (the contraindications) ---------- */

export interface TestsPlan {
  /** asked of everyone this person's answers allow */
  required: TestId[];
  /** offered, not required: the up-and-go and the step test at 60+ */
  optional: TestId[];
  /** left out, each with its reason in the person's words */
  left: { test: TestId; why: string }[];
  /** no tests at all, and why */
  held: string | null;
}

type TestAnswers = Pick<TrainingProfile, "floor" | "joints" | "breathless" | "chairHelp" | "feet" | "eyesTreated">;

function limits(j: JointAnswer | undefined): boolean {
  return !!j && (j.how === "limits" || j.how === "treated");
}

function kneeWords(j: JointAnswer | undefined): string {
  return j?.side === "both" ? "Your knees" : j?.side ? `Your ${j.side} knee` : "Your knee";
}

/**
 * The tests for this person (plan §3.4, §5): at 60+ the chair stand and the 4-stage balance test,
 * the up-and-go and the 2-minute step test optional; under 60 the home tests that place the Kaya
 * programme. Left out, with the reason said: the step test with a knee that limits or is being
 * treated, or breathlessness at rest; the plank for someone who keeps off the floor; the balance
 * test and the up-and-go with no one beside (`alone` — the intro asks); the chair stand when
 * standing needs help (the plan is held for a physiotherapist). The gate at "doctor" holds them
 * all. "Stands only with hands" leaves nothing out: the test records what happened (ADR-054).
 */
export function testsFor(
  S: KayaState,
  a: TestAnswers = S.profile.training ?? {},
  device: DeviceAnswers = {},
  opts: { alone?: boolean } = {},
  today = new Date()
): TestsPlan {
  if (device.trainingGate)
    return {
      required: [],
      optional: [],
      left: [],
      held: "No tests until your doctor says more: you answered yes under Before you start.",
    };
  const left: TestsPlan["left"] = [];
  const knee = a.joints?.knee;
  const out = (test: TestId, why: string) => left.push({ test, why });

  const old = older(S, today);
  let required: TestId[] = old ? ["chair", "balance"] : ["pushups", "plank", "chair", "step"];
  let optional: TestId[] = old ? ["tug", "step"] : [];

  if (a.chairHelp) out("chair", "You need help to stand from a chair: a physiotherapist starts this with you.");
  if (limits(knee)) out("step", `${kneeWords(knee)}: marching on the spot for two minutes is left out.`);
  else if (a.breathless === "rest") out("step", "You get breathless at your own pace: the two-minute march is left out.");
  if (!old && a.floor === "no") out("plank", "You keep off the floor: no plank.");
  // the safety items on Still right? (ADR-053), as the sessions read them (P9)
  if (a.feet === "sore") {
    const why = "A sore on the foot: tests on your feet wait until it heals.";
    for (const t of ["balance", "tug", "step", "pushups", "plank"] as const) if (!left.some((l) => l.test === t)) out(t, why);
  }
  if (a.eyesTreated) {
    if (!left.some((l) => l.test === "step")) out("step", "Eye treatment for diabetes: no all-out march.");
    if (!left.some((l) => l.test === "plank")) out("plank", "Eye treatment for diabetes: no heads-down hold.");
  }
  if (opts.alone) {
    if (old) out("balance", "Balance needs someone beside you: do it when they are.");
    if (old) out("tug", "The up-and-go needs someone beside you too.");
  }

  const gone = new Set(left.map((l) => l.test));
  required = required.filter((t) => !gone.has(t));
  optional = optional.filter((t) => !gone.has(t));
  return { required, optional, left, held: null };
}

/**
 * The tests the questionnaire's step lists (P3, ADR-053): at 60+ the chair stand and the 4-stage
 * balance test; under 60 the home tests that place the Kaya programme — wall push-ups, a plank
 * hold (not for someone who keeps off the floor), the chair stand and the 2-minute step.
 */
export function testsOffered(S: KayaState, a: Pick<TrainingProfile, "floor">, today = new Date()): TestKey[] {
  if (older(S, today)) return ["chair", "balance"];
  return a.floor === "no" ? ["pushups", "chair", "step"] : ["pushups", "plank", "chair", "step"];
}

const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six"];

/** "One short test", "Three short tests": the count of the tests actually offered, never a fixed four. */
export function shortTestsTitle(count: number): string {
  return `${COUNT_WORDS[count] ?? String(count)} short test${count === 1 ? "" : "s"}`;
}

/**
 * Train's "Your tests" row value (P9): shown once there are tests, so the figures are on Train
 * before the retest is due — "9 stands · retest 2 Nov", "retest due now" from the day it is due.
 * Null before any tests.
 */
export function testsRowValue(S: KayaState, today: string): string | null {
  const lt = latestTests(S, today);
  if (!lt) return null;
  const t = lt.tests;
  const figure =
    t.chairStand !== undefined ? `${t.chairStand} stands` : t.pushups !== undefined ? `${t.pushups} push-ups` : null;
  const due = addDays(lt.ds, RETEST_DAYS);
  const when = due <= today ? "retest due now" : `retest ${dayMonth(due)}`;
  return figure ? `${figure} · ${when}` : when;
}

/* ---------- the readings over time ---------- */

function anyResult(t: DayTests | undefined): t is DayTests {
  return (
    !!t &&
    (t.chairStand !== undefined ||
      t.balanceStage !== undefined ||
      t.tug !== undefined ||
      t.step !== undefined ||
      t.pushups !== undefined ||
      t.plank !== undefined)
  );
}

/** The most recent day with a test result, on or before `upTo` when given, or null. The one
 *  implementation: the programme, its views, the questionnaire and the family pages read this. */
export function latestTests(S: KayaState, upTo?: string): { ds: string; tests: DayTests } | null {
  let best: { ds: string; tests: DayTests } | null = null;
  for (const [ds, L] of Object.entries(S.logs)) {
    if ((upTo !== undefined && ds > upTo) || !anyResult(L?.tests)) continue;
    if (!best || ds > best.ds) best = { ds, tests: L.tests };
  }
  return best;
}

/** Retest four weeks on (plan §3.4). */
export const RETEST_DAYS = 28;

export function retestDue(S: KayaState, upTo?: string): string | null {
  const t = latestTests(S, upTo);
  return t ? addDays(t.ds, RETEST_DAYS) : null;
}

/** The figure Progress draws for a test: the count, the seconds, and for the balance test the
 *  seconds held heel to toe (10 once held, 0 when it was not reached). */
export function testValue(test: TestId, t: Partial<DayTests>): number | null {
  switch (test) {
    case "chair":
      return t.chairStand ?? null;
    case "balance":
      if (t.balanceStage === undefined) return null;
      return t.balanceStage >= 3 ? TANDEM_SECS : t.balanceStage === 2 ? (t.balanceSecs ?? 0) : 0;
    case "tug":
      return t.tug ?? null;
    case "step":
      return t.step ?? null;
    case "pushups":
      return t.pushups ?? null;
    case "plank":
      return t.plank ?? null;
  }
}

/** One test's readings, oldest first — the readings the person did, nothing smoothed. */
export function testSeries(S: KayaState, test: TestId): { ds: string; value: number }[] {
  const out: { ds: string; value: number }[] = [];
  for (const [ds, L] of Object.entries(S.logs)) {
    if (!L.tests) continue;
    const v = testValue(test, L.tests);
    if (v !== null) out.push({ ds, value: v });
  }
  return out.sort((a, b) => (a.ds < b.ds ? -1 : 1));
}

/** Days with any test result. */
export function testDays(S: KayaState): number {
  return Object.values(S.logs).filter((L) => anyResult(L.tests)).length;
}

/* ---------- placement ---------- */

export interface Placement {
  level: "steady" | "build";
  /** "Steady: 9 chair stands, tandem 6 s" */
  line: string;
  /** which result or answer placed it, for the plan line */
  by: "chair" | "tandem" | "tug" | "falls" | "unsteady" | null;
}

type PlaceAnswers = Pick<TrainingProfile, "falls" | "unsteady">;

/**
 * Steady or Build at 60+ (plan §3.4): a chair-stand count under the STEADI line, a heel-to-toe
 * (tandem) stand not held for 10 s, a TUG of 12 s or more, a fall in the last year, or feeling
 * unsteady places Steady; anything else Build. Null under 60, where the Kaya programme keeps its
 * calendar phase (or the home tests' baseline, `ownerPhase`). Never a grade: the line is the
 * figures.
 */
export function placement(
  S: KayaState,
  t: Partial<DayTests>,
  today = new Date(),
  a: PlaceAnswers = S.profile.training ?? {}
): Placement | null {
  if (!older(S, today)) return null;
  const floor = chairStandLine(S, today);
  const lowChair = t.chairStand !== undefined && floor !== null && t.chairStand < floor;
  const lowTandem = t.balanceStage !== undefined && t.balanceStage < 3;
  const lowTug = t.tug !== undefined && t.tug >= TUG_LINE;
  const fell = a.falls !== undefined && a.falls !== "none";
  const unsteady = a.unsteady !== undefined && a.unsteady !== "no";
  const level = lowChair || lowTandem || lowTug || fell || unsteady ? "steady" : "build";
  const parts: string[] = [];
  if (t.chairStand !== undefined) parts.push(`${t.chairStand} chair stands`);
  if (t.balanceStage !== undefined)
    parts.push(
      t.balanceStage >= 3 ? "tandem 10 s" : t.balanceStage === 2 ? `tandem ${t.balanceSecs ?? 0} s` : "tandem not reached"
    );
  if (t.tug !== undefined) parts.push(`up-and-go ${t.tug} s`);
  return {
    level,
    line: `${level === "steady" ? "Steady" : "Build"}: ${parts.length ? parts.join(", ") : "from your answers"}`,
    by: lowChair ? "chair" : lowTandem ? "tandem" : lowTug ? "tug" : fell ? "falls" : unsteady ? "unsteady" : null,
  };
}

/** The level in one sentence, as the result screen's hero says it. */
export function levelSentence(level: Placement["level"]): string {
  return level === "steady"
    ? "Steady: standing moves at the counter, no unsupported one-leg stands."
    : "Build: strength and balance three days, with walks between.";
}

/** Why, in one sentence: "Placed by 9 chair stands (the range starts at 11) and a 6-second tandem stand (the line is 10)." */
export function placementReason(S: KayaState, p: Placement, t: Partial<DayTests>, today = new Date()): string {
  const line = chairStandLine(S, today);
  const bits: string[] = [];
  if (t.chairStand !== undefined && line !== null)
    bits.push(`${n(t.chairStand, "chair stand", "chair stands")} (the range starts at ${line})`);
  if (t.balanceStage !== undefined)
    bits.push(
      t.balanceStage >= 3
        ? `a ${TANDEM_SECS}-second tandem stand (the line is ${TANDEM_SECS})`
        : t.balanceStage === 2
          ? `a ${t.balanceSecs ?? 0}-second tandem stand (the line is ${TANDEM_SECS})`
          : `a tandem stand not reached (the line is ${TANDEM_SECS} seconds)`
    );
  if (t.tug !== undefined) bits.push(`a ${t.tug}-second up-and-go (the line is ${TUG_LINE})`);
  if (p.by === "falls") bits.push("a fall in the last year");
  if (p.by === "unsteady") bits.push("feeling unsteady at times");
  if (!bits.length) return "Placed by your answers.";
  const words = bits.length === 1 ? bits[0] : `${bits.slice(0, -1).join(", ")} and ${bits[bits.length - 1]}`;
  return `Placed by ${words}.`;
}

/**
 * The owner's home tests place the Kaya programme instead of week 1 (plan §3.4). No published norm
 * exists for either test, so this rule is Kaya's own (ADR-054): a plank of 40 s or more with 12 or
 * more wall push-ups → phase index 2; 20 s and 8 → index 1; otherwise 0. Without a plank (someone
 * who keeps off the floor) the push-ups alone reach index 1 at 8, never 2. Null without push-ups.
 */
export const OWNER_RULE = { high: { plank: 40, pushups: 12 }, mid: { plank: 20, pushups: 8 } } as const;

export function ownerPhase(t: Partial<DayTests>): PhaseIndex | null {
  if (t.pushups === undefined) return null;
  if (t.plank === undefined) return t.pushups >= OWNER_RULE.mid.pushups ? 1 : 0;
  if (t.plank >= OWNER_RULE.high.plank && t.pushups >= OWNER_RULE.high.pushups) return 2;
  if (t.plank >= OWNER_RULE.mid.plank && t.pushups >= OWNER_RULE.mid.pushups) return 1;
  return 0;
}

/**
 * What the home tests write to `profile.phaseMode`: the placed index as a pin, only on the Kaya
 * programme under 60, only while the phase is "auto" (a phase the person pinned is theirs), and
 * only when it differs from where the calendar already is — an agreeing result leaves the calendar
 * running. `calendar` is `plan.ts::phase(S)`. Null: nothing to write.
 */
export function ownerPhaseMode(S: KayaState, t: Partial<DayTests>, calendar: PhaseIndex, today = new Date()): string | null {
  if (older(S, today)) return null;
  if ((S.profile.training?.programme ?? "kaya") !== "kaya") return null;
  if (S.profile.phaseMode !== "auto") return null;
  const placed = ownerPhase(t);
  if (placed === null || placed === calendar) return null;
  return String(placed);
}

/* ---------- Progress › Training's Tests line ---------- */

/**
 * The chapter's "Tests" insight (ADR-033's shape): the latest chair-stand count against the one
 * before and the range, and the retest date; under 60 the home tests as figures with no range.
 * Null when there is no reading yet.
 */
export function testsInsight(S: KayaState, today: string, now = new Date()): Insight | null {
  const last = latestTests(S);
  if (!last) return null;
  const due = addDays(last.ds, RETEST_DAYS);
  const dueWords = due <= today ? "a retest is due now" : `retest due ${dayLabel(due)}`;
  const days = testDays(S);
  const link = { to: "/progress/training/tests", label: "Your tests" };
  const years = age(S, now);
  const chair = testSeries(S, "chair");
  const t = last.tests;

  if (t.chairStand !== undefined && chair.length) {
    const cur = chair[chair.length - 1];
    const prev = chair.length > 1 ? chair[chair.length - 2] : null;
    const r = rangeFor("chair", years, S.profile.sex);
    const head = prev
      ? `${n(cur.value, "chair stand", "chair stands")} on ${dayMonth(cur.ds)}, from ${prev.value} on ${dayMonth(prev.ds)}`
      : `${n(cur.value, "chair stand", "chair stands")} on ${dayMonth(cur.ds)}`;
    const range = r ? `; the usual range starts at ${r.low}` : "";
    return {
      key: "training.tests",
      text: `${head}${range}. ${dueWords.charAt(0).toUpperCase()}${dueWords.slice(1)}.`,
      basedOn: basedOn(n(days, "test day", "test days"), r ? `${r.source}, ${r.who} ${r.band}` : null),
      link,
    };
  }
  const bits: string[] = [];
  if (t.pushups !== undefined) bits.push(n(t.pushups, "wall push-up", "wall push-ups"));
  if (t.plank !== undefined) bits.push(`a ${t.plank}-second plank`);
  if (t.step !== undefined) bits.push(n(t.step, "step", "steps"));
  if (!bits.length) return null;
  const words = bits.length === 1 ? bits[0] : `${bits.slice(0, -1).join(", ")} and ${bits[bits.length - 1]}`;
  return {
    key: "training.tests",
    text: `${words} on ${dayMonth(last.ds)}: your figures, no published range. ${dueWords.charAt(0).toUpperCase()}${dueWords.slice(1)}.`,
    basedOn: basedOn(n(days, "test day", "test days")),
    link,
  };
}

/** "in 24 days" / "today" / "3 days ago" for the retest row. */
export function dueIn(due: string, today: string): string {
  const d = daysBetween(today, due);
  return d === 0 ? "today" : d > 0 ? `in ${n(d, "day", "days")}` : `${n(-d, "day", "days")} ago`;
}
